import { PageKey } from '@core/enums/page-keys.enum';
import { TraineeType } from '@core/enums/trainee-type.enum';
import { VisitorType } from '@core/enums/visitor-type.enum';
import {
  FlowId,
  type CheckinWorkflow,
  type StepResult,
  type WorkflowVariables,
} from '../workflow-engine.types';
import { checkinFlow } from './checkin.flow';
import { checkinSummaryFlow } from './checkin-summary.flow';
import { covidAcknowledgementFlow } from './covid-acknowledgement.flow';
import { exportControlValidationFlow } from './export-control-validation.flow';
import { facialRecognitionFlow } from './facial-recognition.flow';
import { printBadgeFlow } from './print-badge.flow';
import { traineeTypeSelectionFlow } from './trainee-type-selection.flow';
import { trainingSummaryFlow } from './training-summary.flow';

export enum ManualFlowStep {
  Start = 'Start',
  AfterCovidAcknowledgement = 'AfterCovidAcknowledgement',
  AfterTraineeType = 'AfterTraineeType',
  FacialRecognitionGate = 'FacialRecognitionGate',
  AfterFacialRecognition = 'AfterFacialRecognition',
  CustomerSelection = 'CustomerSelection',
  TrainingSearch = 'TrainingSearch',
  TrainingSummary = 'TrainingSummary',
  AfterTrainingSummary = 'AfterTrainingSummary',
  YourInformation = 'YourInformation',
  MoreInformationGate = 'MoreInformationGate',
  MoreInformation = 'MoreInformation',
  ExportControlGate = 'ExportControlGate',
  AfterExportControl = 'AfterExportControl',
  CreateAccount = 'CreateAccount',
  Checkin = 'Checkin',
  AfterCheckin = 'AfterCheckin',
  AfterPrintBadge = 'AfterPrintBadge',
  CheckinSummary = 'CheckinSummary',
  AfterCheckinSummary = 'AfterCheckinSummary',
  Survey = 'Survey',
}

function needsTrainingSelection(variables: WorkflowVariables): boolean {
  return (
    variables.checkinProcessConfiguration?.hasReservation === true &&
    variables.visitorType === VisitorType.Trainee &&
    variables.traineeType !== TraineeType.CADET &&
    variables.traineeType !== TraineeType.OTHER
  );
}

function backBeforeIdentity(variables: WorkflowVariables): StepResult<ManualFlowStep> {
  if (variables.visitorType === VisitorType.Trainee) {
    return { nextStep: ManualFlowStep.AfterCovidAcknowledgement };
  }
  return variables.checkinProcessConfiguration?.hasCoronavirusScreen
    ? { nextStep: ManualFlowStep.Start }
    : { bubble: 'back' };
}

function backBeforeTraining(variables: WorkflowVariables): StepResult<ManualFlowStep> {
  return variables.checkinProcessConfiguration?.hasFacialRecognition
    ? { nextStep: ManualFlowStep.FacialRecognitionGate }
    : backBeforeIdentity(variables);
}

export const manualFlow: CheckinWorkflow<ManualFlowStep> = {
  id: FlowId.Manual,
  entry: ManualFlowStep.Start,
  steps: {
    [ManualFlowStep.Start]: {
      id: ManualFlowStep.Start,
      next: (variables) =>
        variables.checkinProcessConfiguration?.hasCoronavirusScreen
          ? {
              flow: covidAcknowledgementFlow,
              returnStep: ManualFlowStep.AfterCovidAcknowledgement,
            }
          : { nextStep: ManualFlowStep.AfterCovidAcknowledgement },
      back: () => ({ bubble: 'back' }),
    },
    [ManualFlowStep.AfterCovidAcknowledgement]: {
      id: ManualFlowStep.AfterCovidAcknowledgement,
      next: (variables) =>
        variables.visitorType === VisitorType.Trainee
          ? { flow: traineeTypeSelectionFlow, returnStep: ManualFlowStep.AfterTraineeType }
          : { nextStep: ManualFlowStep.FacialRecognitionGate },
      back: (variables) =>
        variables.checkinProcessConfiguration?.hasCoronavirusScreen
          ? { nextStep: ManualFlowStep.Start }
          : { bubble: 'back' },
    },
    [ManualFlowStep.AfterTraineeType]: {
      id: ManualFlowStep.AfterTraineeType,
      next: () => ({ nextStep: ManualFlowStep.FacialRecognitionGate }),
    },
    [ManualFlowStep.FacialRecognitionGate]: {
      id: ManualFlowStep.FacialRecognitionGate,
      next: (variables) =>
        variables.checkinProcessConfiguration?.hasFacialRecognition
          ? { flow: facialRecognitionFlow, returnStep: ManualFlowStep.AfterFacialRecognition }
          : { nextStep: ManualFlowStep.AfterFacialRecognition },
      back: backBeforeIdentity,
      exit: () => ({ bubble: 'exit' }),
    },
    [ManualFlowStep.AfterFacialRecognition]: {
      id: ManualFlowStep.AfterFacialRecognition,
      next: (variables) => {
        return needsTrainingSelection(variables)
          ? { nextStep: ManualFlowStep.CustomerSelection }
          : { nextStep: ManualFlowStep.YourInformation };
      },
    },
    [ManualFlowStep.CustomerSelection]: {
      id: ManualFlowStep.CustomerSelection,
      page: PageKey.CUSTOMERS,
      next: () => ({ nextStep: ManualFlowStep.TrainingSearch }),
      back: backBeforeTraining,
      exit: () => ({ bubble: 'exit' }),
    },
    [ManualFlowStep.TrainingSearch]: {
      id: ManualFlowStep.TrainingSearch,
      page: PageKey.TRAINING_SEARCH,
      next: () => ({ nextStep: ManualFlowStep.TrainingSummary }),
      back: () => ({ nextStep: ManualFlowStep.CustomerSelection }),
      exit: () => ({ bubble: 'exit' }),
    },
    [ManualFlowStep.TrainingSummary]: {
      id: ManualFlowStep.TrainingSummary,
      next: () => ({ flow: trainingSummaryFlow, returnStep: ManualFlowStep.AfterTrainingSummary }),
      back: () => ({ nextStep: ManualFlowStep.CustomerSelection }),
      exit: () => ({ bubble: 'exit' }),
    },
    [ManualFlowStep.AfterTrainingSummary]: {
      id: ManualFlowStep.AfterTrainingSummary,
      next: () => ({ nextStep: ManualFlowStep.YourInformation }),
    },
    [ManualFlowStep.YourInformation]: {
      id: ManualFlowStep.YourInformation,
      page: PageKey.VISITOR_INFORMATION_ABOUTYOU,
      next: () => ({ nextStep: ManualFlowStep.MoreInformationGate }),
      back: (variables) => {
        if (needsTrainingSelection(variables)) {
          return { nextStep: ManualFlowStep.TrainingSummary };
        }
        return backBeforeTraining(variables);
      },
      exit: () => ({ bubble: 'exit' }),
    },
    [ManualFlowStep.MoreInformationGate]: {
      id: ManualFlowStep.MoreInformationGate,
      next: (variables) =>
        variables.hasExtraInformationPage
          ? { nextStep: ManualFlowStep.MoreInformation }
          : { nextStep: ManualFlowStep.ExportControlGate },
    },
    [ManualFlowStep.MoreInformation]: {
      id: ManualFlowStep.MoreInformation,
      page: PageKey.VISITOR_INFORMATION_MORE_ABOUTYOU,
      next: () => ({ nextStep: ManualFlowStep.ExportControlGate }),
      back: () => ({ nextStep: ManualFlowStep.YourInformation }),
      exit: () => ({ bubble: 'exit' }),
    },
    [ManualFlowStep.ExportControlGate]: {
      id: ManualFlowStep.ExportControlGate,
      next: (variables) =>
        variables.checkinProcessConfiguration?.hasExportControl
          ? { flow: exportControlValidationFlow, returnStep: ManualFlowStep.AfterExportControl }
          : { nextStep: ManualFlowStep.AfterExportControl },
      exit: () => ({ bubble: 'exit' }),
    },
    [ManualFlowStep.AfterExportControl]: {
      id: ManualFlowStep.AfterExportControl,
      next: (variables) =>
        variables.visitorType === VisitorType.Trainee &&
        variables.checkinProcessConfiguration?.hasCAEConnectFlow
          ? { nextStep: ManualFlowStep.CreateAccount }
          : { nextStep: ManualFlowStep.Checkin },
    },
    [ManualFlowStep.CreateAccount]: {
      id: ManualFlowStep.CreateAccount,
      page: PageKey.CREATE_ACCOUNT,
      next: () => ({ nextStep: ManualFlowStep.Checkin }),
      back: () => ({ nextStep: ManualFlowStep.YourInformation }),
      exit: () => ({ bubble: 'exit' }),
    },
    [ManualFlowStep.Checkin]: {
      id: ManualFlowStep.Checkin,
      next: () => ({ flow: checkinFlow, returnStep: ManualFlowStep.AfterCheckin }),
      exit: () => ({ bubble: 'exit' }),
    },
    [ManualFlowStep.AfterCheckin]: {
      id: ManualFlowStep.AfterCheckin,
      next: (variables) =>
        variables.checkinProcessConfiguration?.hasPrintBadge && variables.isCheckinSuccess
          ? { flow: printBadgeFlow, returnStep: ManualFlowStep.AfterPrintBadge }
          : { nextStep: ManualFlowStep.CheckinSummary },
    },
    [ManualFlowStep.AfterPrintBadge]: {
      id: ManualFlowStep.AfterPrintBadge,
      next: () => ({ nextStep: ManualFlowStep.CheckinSummary }),
    },
    [ManualFlowStep.CheckinSummary]: {
      id: ManualFlowStep.CheckinSummary,
      next: () => ({ flow: checkinSummaryFlow, returnStep: ManualFlowStep.AfterCheckinSummary }),
      exit: () => ({ bubble: 'exit' }),
    },
    [ManualFlowStep.AfterCheckinSummary]: {
      id: ManualFlowStep.AfterCheckinSummary,
      next: (variables) =>
        variables.checkinProcessConfiguration?.hasSurvey
          ? { nextStep: ManualFlowStep.Survey }
          : { complete: true },
    },
    [ManualFlowStep.Survey]: {
      id: ManualFlowStep.Survey,
      page: PageKey.SURVEY,
      next: () => ({ complete: true }),
    },
  },
};
